import _ from 'lodash';
import classNames from 'classnames';
import INotification, {
  INotificationSeverity,
  INotificationSeverityColor,
  INotificationOrder,
  INotificationOrderMap,
} from '@scale/shared/types/notifications/notification';
import INotificationSend, {
  DEFAULT_NOTIFICATION_LIMIT,
} from '@scale/shared/types/notifications/notificationSend';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Badge, Button, makeStyles, Tab, Tabs, ToggleButtonGroup } from 'design-system';
import Notification from '../../UI/Notification/Notification';
import { useNotificationsStore } from './NotificationsStore';
import apiFetch from 'react-scale/lib/api-fetch';

const useStyles = makeStyles(() => ({
  info: {
    backgroundColor: INotificationSeverityColor.info,
    color: '#fff',
  },
  warning: {
    backgroundColor: INotificationSeverityColor.warning,
    color: '#fff',
  },
  action_required: {
    backgroundColor: INotificationSeverityColor.action_required,
    color: '#fff',
  },
  urgent: {
    backgroundColor: INotificationSeverityColor.urgent,
    color: '#fff',
  },
  notificationsTabs: {
    margin: '20px 0',
  },
}));

const Notifications: React.FC<{
  collapsed: boolean;
  setNotificationCount: (count: Record<keyof typeof INotificationSeverity, number>) => void;
}> = ({ collapsed, setNotificationCount }) => {
  const classes = useStyles();
  const {
    currentTab,
    hasMore,
    dismissingAll,
    dismissAll,
    setDismissingAll,
    incrementSkip,
    initialFetch,
    initialUncollapsedFetch,
    latestDate,
    notifications,
    setDismissNotification,
    setHasMore,
    setInitialUncollapsedFetch,
    setNotifications,
    setSetting,
    setTab,
    setting,
    skip,
    toggleInitialFetch,
  } = useNotificationsStore();
  const notificationContainerRef = useRef<HTMLDivElement>(null);
  const fetchPage = useCallback(async () => {
    const response = await apiFetch(
      null,
      `corp-api/notifications?skip=${skip}&limit=${DEFAULT_NOTIFICATION_LIMIT}`,
    );
    const notifications = response.notifications || response;
    if (notifications.length < DEFAULT_NOTIFICATION_LIMIT) {
      setHasMore(false);
    }
    incrementSkip(notifications.length);
    setNotifications(notifications);
  }, [incrementSkip, setHasMore, setNotifications, skip]);

  const fetchByLatestDate = useCallback(async () => {
    const response = await apiFetch(
      null,
      `corp-api/notifications?latestDate=${latestDate}&limit=${DEFAULT_NOTIFICATION_LIMIT}`,
    );
    const notifications = response.notifications || response;
    incrementSkip(notifications.length);
    setNotifications(notifications);
  }, [incrementSkip, latestDate, setNotifications]);

  const dismissNotification = useCallback(
    async (notificationId: string) => {
      await apiFetch(null, `corp-api/notifications/dismiss/${notificationId}`, {
        method: 'PUT',
      });
      setDismissNotification(notificationId);
    },
    [setDismissNotification],
  );

  const dismissNotificationsBySeverity = useCallback(async () => {
    setDismissingAll(true);
    const severity = INotificationOrder[currentTab];
    await apiFetch(null, 'corp-api/notifications/dismiss', {
      method: 'PUT',
      body: { filters: { severity } },
    });

    dismissAll(severity);
    setDismissingAll(false);
  }, [currentTab, dismissAll, setDismissingAll]);

  // effect that handles when to fetch notifications
  useEffect(() => {
    if (collapsed && initialFetch) {
      fetchPage();
      toggleInitialFetch();
    }

    if (collapsed && !initialFetch) return;

    if (!collapsed && initialUncollapsedFetch && hasMore) {
      if (!latestDate) {
        fetchPage();
      } else {
        fetchByLatestDate();
      }
      setInitialUncollapsedFetch(false);
    }
  }, [
    hasMore,
    latestDate,
    collapsed,
    fetchPage,
    fetchByLatestDate,
    initialFetch,
    toggleInitialFetch,
    initialUncollapsedFetch,
    setInitialUncollapsedFetch,
  ]);

  // effect that resets state when collapsed
  useEffect(() => {
    if (collapsed) {
      setInitialUncollapsedFetch(true);
      setSetting('unread');
    }
  }, [collapsed, setInitialUncollapsedFetch, setSetting]);

  const severityRelatedNotifications = useMemo(() => {
    const showAllNotifs = setting !== 'unread';
    const sortedNotifications = _.filter(notifications, notif =>
      showAllNotifs ? true : _.isUndefined(notif.dismissedAt),
    );

    return _.reduce(
      sortedNotifications,
      (acc, { notification, ...rest }) => {
        if (!notification.severity) return acc;
        if (!acc[notification.severity]) {
          acc[notification.severity] = [{ notification, ...rest }];
        } else {
          acc[notification.severity].push({ notification, ...rest });
        }
        return acc;
      },
      {} as Record<string, (INotificationSend & { notification: INotification })[]>,
    );
  }, [notifications, setting]);

  // effect that sets the tab according to the notification count
  useEffect(() => {
    const currentSeverity = INotificationOrder[currentTab];
    if (!severityRelatedNotifications[currentSeverity]?.length && collapsed) {
      const newTab = _.reduce(
        _.entries(severityRelatedNotifications),
        (acc, [severity, notifs]) => {
          if (
            notifs.length &&
            INotificationOrderMap[severity as keyof typeof INotificationSeverity] > acc
          )
            return INotificationOrderMap[severity as keyof typeof INotificationSeverity];
          return acc;
        },
        -1,
      );

      if (newTab >= 0) {
        setTab(newTab);
      }
    }
  }, [collapsed, currentTab, setHasMore, setTab, severityRelatedNotifications]);

  // effect that sets the notification count
  useEffect(() => {
    if (setting !== 'unread') return;
    const notificationsCount: Record<keyof typeof INotificationSeverity, number> = {
      info: 0,
      warning: 0,
      action_required: 0,
      urgent: 0,
    };

    for (const [severity, list] of _.entries(severityRelatedNotifications)) {
      notificationsCount[severity as keyof typeof INotificationSeverity] = list.length;
    }
    setNotificationCount(notificationsCount);
  }, [setNotificationCount, severityRelatedNotifications, setting]);

  return (
    <div ref={notificationContainerRef} className={classNames('notifications', { collapsed })}>
      <div className="notifications__container">
        <a
          target="__blank"
          href="https://scale.atlassian.net/wiki/spaces/EPD/pages/136020101/Notifications"
        >
          Want to add your own notification? Find out how here!
        </a>
        <div className="notifications__header">
          <h2>Your Notifications</h2>
          <div className="notifications__actions">
            <ToggleButtonGroup
              version="2"
              value={setting}
              onChange={setSetting}
              options={[
                { value: 'unread', label: 'Only unread' },
                { value: 'all', label: 'All' },
              ]}
            />
          </div>
        </div>
        <Tabs
          className={classes.notificationsTabs}
          onClick={setTab}
          defaultActiveTabIndex={currentTab}
        >
          {_.entries(INotificationSeverity).map(([severity, severityName]) => (
            <Tab
              key={severity}
              label={severityName}
              disabled={!severityRelatedNotifications[severity]?.length}
              icon={
                <div className="notificationsBadge">
                  <Badge
                    classes={{
                      badge: classes[severity as keyof typeof classes],
                    }}
                    max={99}
                    badgeContent={severityRelatedNotifications[severity]?.length ?? 0}
                  />
                </div>
              }
            />
          ))}
        </Tabs>
        <Button type="secondary" onClick={dismissNotificationsBySeverity}>
          Dismiss All
        </Button>
        <div className="notifications__body">
          {severityRelatedNotifications[INotificationOrder[currentTab]]?.map(
            (notification: INotificationSend & { notification: INotification }) => (
              <Notification
                disabled={dismissingAll}
                dismissNotification={dismissNotification}
                key={notification._id}
                notificationObject={notification}
              />
            ),
          )}
          {hasMore && <Button onClick={fetchPage}>Load more</Button>}
        </div>
      </div>
      <style jsx>{`
        .notifications {
          flex-shrink: 0;
          overflow: auto;
          font-size: 14px;
          line-height: 2;
          transition: width 125ms ease-in-out;
          width: 581px;
          position: absolute;
          margin-top: 60px;
          display: flex;
          flex-direction: column;
          visibility: visible;
          top: 1px;
          right: 0;
          background: #fff;
          height: calc(100vh - 61px);
          border-left: 1px solid var(--color-RemoGray10);
          // above the 'wiki' and 'stats' links
          z-index: 1002;
        }

        .notifications.collapsed {
          width: 0;
        }

        .notifications.collapsed .notifications__container {
          display: none;
        }

        .notifications__container {
          padding: 8px 16px 16px;
        }

        .notifications__container > a {
          font-style: italic;
          font-weight: bold;
        }

        .notifications__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .notifications__header > h2 {
          margin: 0;
          line-height: 1.2;
        }

        .notifications__actions {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .notifications__actions label {
          cursor: pointer;
          display: block;
          margin-left: 8px;
          max-width: 110px;
          line-height: 1.2;
        }

        .notifications__body {
          margin: 24px 0 16px;
        }

        .notifications__item-header {
          display: flex;
          align-items: center;
        }
        .notifications__item-header-icon {
          width: 16px;
          height: 16px;
          border-radius: 50px;
          margin-right: 8px;
        }

        .notifications__item {
          border-radius: 4px;
          padding: 12px;
          background: var(--color-RemoGray10);
          margin-bottom: 16px;
          max-height: 635px;
          overflow: scroll;
        }

        .notifications__item-list h3 {
          font-weight: normal;
        }

        .notifications__item h3 {
          margin: 0;
          line-height: 1.2;
          margin: 16px 0;
        }

        .notificationsBadge {
          margin-left: 7px;
          margin-right: 20px;
        }
      `}</style>
    </div>
  );
};

export default Notifications;
